// ─────────────────────────────────────────────────────────────────────────────
// src/features/mdx/components/SaveQueryModal.tsx
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../../shared/store/hooks";
import { createSavedQuery } from "../api"; // ← apiClient via endpoints
import { FONTS, colors } from "../../../shared/ui/tokens";

// ── Types partagés pour Comparaison et Évolution ─────────────────────────────
export interface ComparisonPeriod {
  label: string;
  dateFrom: string;
  dateTo: string;
  // ▼ solde et démarque par période (pour l'enregistrement en BD) ▼
  soldeCodePeriode?: string | null;
  demarquePhase?: string | null;
}

export interface EvolutionConfig {
  dateFrom: string;
  dateTo: string;
  granularity: string;
}

interface Props {
  onClose: () => void;
  onSaved?: (id: number) => void;
  // ── Type et section de la vue (article | sousfamille | saison | mdx) ───────
  type?: string;
  section?: string;
  // ── Overrides pour les vues locales (VentesView, SousFamilleView…) ─────────
  overrideMeasures?: string[];
  overrideDateFrom?: string | null;
  overrideDateTo?: string | null;
  overrideTopN?: number | null;
  // ── Dimensions : permet de forcer les lignes/colonnes (ex: ComparisonView) ──
  overrideRowsDimensions?: any[];
  overrideColumnsDimensions?: any[];
  // ── Solde : code de la période de solde sélectionnée ──────────────────────
  overrideSoldeCodePeriode?: string | null;
  overrideDemarquePhase?: string | null;

  // ── Comparaison : liste des périodes définies (enrichies) ─────────────────
  overrideComparisonPeriods?: ComparisonPeriod[] | null;
  // ── Évolution : config granularité + plage globale ─────────────────────────
  overrideEvolutionConfig?: EvolutionConfig | null;
}

// ── AVDASH palette ────────────────────────────────────────────────────────────
const NW = {
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetMed: colors.brand[100],
  violetLight: colors.brand[100],
  violetSoft: colors.neutral[100],
  rose: colors.accent[300],
  white: colors.neutral[0],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray300: colors.neutral[300],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
  success: colors.accent[500],
  successLight: colors.accent[50],
  teal: colors.accent[300],
  tealLight: colors.accent[100],
  amber: colors.warning[500],
  amberLight: colors.warning[100],
};

// ── Labels lisibles pour type et section ─────────────────────────────────────
const TYPE_LABELS: Record<
  string,
  { label: string; icon: string; color: string }
> = {
  article: { label: "Groupes Article", icon: "📦", color: "#184B8C" },
  magazin: { label: "Magazine", icon: "🛍️", color: "#7DA8D9" },
  couleur: { label: "Couleur", icon: "🎨", color: "#1D5BA8" },
  "couleur-taille": { label: "Couleur & Taille", icon: "🎨📏", color: "#4A86C7" }, // ✅ NOUVEAU
  famille: { label: "Famille", icon: "👪", color: "#1D5BA8" },
  Marque: { label: "Marque", icon: "🏷️", color: "#7DA8D9" },
  Saison: { label: "Saison", icon: "⟐", color: "#00A08C" },
  taille: { label: "Taille", icon: "📏", color: "#D98E04" },
  vendeur: { label: "Vendeur", icon: "🧑‍💼", color: "#00A08C" },
  sousfamille: { label: "Sous Famille", icon: "🏷️", color: "#1D5BA8" },
  saison: { label: "Saison", icon: "⟐", color: "#00A08C" },
  groupe: { label: "Groupe", icon: "⊞", color: "#2077BC" },
  univers: { label: "Univers", icon: "◍", color: "#00806F" },
  mdx: { label: "MDX Builder", icon: "⬡", color: "#4A86C7" },
};

const SECTION_LABELS: Record<
  string,
  { label: string; icon: string; color: string }
> = {
  tableau: { label: "Tableau", icon: "📋", color: "#334155" },
  table: { label: "Tableau", icon: "📋", color: "#334155" },
  dashboard: { label: "Dashboard", icon: "📊", color: "#184B8C" },
  comparaison: { label: "Comparaison", icon: "⚖️", color: "#1D5BA8" },
  comparison: { label: "Comparaison", icon: "⚖️", color: "#1D5BA8" },
  evolution: { label: "Évolution", icon: "📈", color: "#00A08C" },
  chart: { label: "Graphique", icon: "📊", color: "#D98E04" },
};

// ── SaveQueryModal ────────────────────────────────────────────────────────────
const SaveQueryModal: React.FC<Props> = ({
  onClose,
  onSaved,
  type,
  section,
  overrideMeasures,
  overrideDateFrom,
  overrideDateTo,
  overrideTopN,
  overrideRowsDimensions,
  overrideColumnsDimensions,
  overrideSoldeCodePeriode,
  overrideDemarquePhase,
  overrideComparisonPeriods,
  overrideEvolutionConfig,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // ── Lecture du state Redux (fallback si pas d'override) ───────────────────
  const reduxMeasures = useAppSelector((s) => s.query.measures);
  const reduxRows = useAppSelector((s) => s.query.rows);
  const reduxColumns = useAppSelector((s) => s.query.columns);
  const reduxDateFrom = useAppSelector((s) => s.query.dateFrom);
  const reduxDateTo = useAppSelector((s) => s.query.dateTo);
  const reduxTopN = useAppSelector((s) => s.query.topN);
  const allMeasures = useAppSelector((s) => s.cube.measures);

  // Utiliser les overrides si fournis, sinon Redux
  const effectiveMeasures = overrideMeasures ?? reduxMeasures;
  const effectiveDateFrom =
    overrideDateFrom !== undefined ? overrideDateFrom : reduxDateFrom;
  const effectiveDateTo =
    overrideDateTo !== undefined ? overrideDateTo : reduxDateTo;
  const effectiveTopN = overrideTopN !== undefined ? overrideTopN : reduxTopN;
  const effectiveSoldeCodePeriode = overrideSoldeCodePeriode ?? null;

  // ── Dimensions : override si fourni (ComparisonView), sinon Redux ────────
  const effectiveRows =
    overrideRowsDimensions !== undefined ? overrideRowsDimensions : reduxRows;
  const effectiveColumns =
    overrideColumnsDimensions !== undefined
      ? overrideColumnsDimensions
      : reduxColumns;

  const [name, setName] = useState("");
  const [remarque, setRemarque] = useState(""); // ← NOUVEAU

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fermer sur clic extérieur
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node))
        onClose();
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [onClose]);

  // Fermer sur Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // ── Sauvegarde via apiClient ──────────────────────────────────────────────
  const handleSave = async () => {
    if (!name.trim()) {
      setError("Veuillez entrer le nom de la requête sauvegardée.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { data } = await createSavedQuery({
        name: name.trim(),
        cubeName: "Maxi-cube",
        measures: effectiveMeasures,
        rowsDimensions: effectiveRows,
        columnsDimensions: effectiveColumns,
        dateFrom: effectiveDateFrom || null,
        dateTo: effectiveDateTo || null,
        topN: effectiveTopN ?? null,
        type: type ?? null,
        section: section ?? null,
        soldeCodePeriode: effectiveSoldeCodePeriode ?? null,
        demarquePhase: overrideDemarquePhase ?? null,
        // ── Périodes de comparaison enrichies : label + dates + solde + démarque ──
        comparisonPeriods: overrideComparisonPeriods
          ? overrideComparisonPeriods.map((p) => ({
              label: p.label,
              dateFrom: p.dateFrom,
              dateTo: p.dateTo,
              soldeCodePeriode: p.soldeCodePeriode ?? null,
              demarquePhase: p.demarquePhase ?? null,
            }))
          : null,
        evolutionConfig: overrideEvolutionConfig ?? null,
        remarque: remarque.trim() || null, // ← NOUVEAU
      } as any);
      setSuccess(true);
      onSaved?.(data.id);
      setTimeout(() => onClose(), 1400);
    } catch (e: any) {
      setError(e.message ?? "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // ── Helpers d'affichage ───────────────────────────────────────────────────
  const getMeasureCaption = (n: string) => {
    const m = allMeasures.find((x) => x.name === n);
    return m?.caption ?? n;
  };
  const getDimLabel = (sel: any) =>
    sel.caption ?? sel.hierarchyName ?? sel.dimensionName ?? "—";

  const typeInfo = type ? TYPE_LABELS[type] : undefined;
  const sectionInfo = section ? SECTION_LABELS[section] : undefined;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(13,42,80,0.45)",
          backdropFilter: "blur(3px)",
          zIndex: 9998,
          animation: "nwFadeIn 0.18s ease",
        }}
      />

      {/* Carte modale */}
      <div
        ref={modalRef}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          zIndex: 9999,
          background: NW.white,
          borderRadius: 20,
          boxShadow:
            "0 24px 80px rgba(24,75,140,0.22),0 4px 20px rgba(15,23,42,0.12)",
          width: "min(820px,96vw)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: FONTS.body,
          animation: "nwSlideUp 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            background: `linear-gradient(135deg,${NW.violetDark} 0%,${NW.violet} 55%,#1D5BA8 100%)`,
            padding: "18px 22px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: `linear-gradient(90deg,transparent,${NW.rose}AA,${NW.rose}DD,${NW.rose}AA,transparent)`,
            }}
          />
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 11,
              background: "rgba(255,255,255,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              flexShrink: 0,
              boxShadow: "0 0 0 1px rgba(255,255,255,0.18)",
            }}
          >
            💾
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 800,
                fontSize: 16,
                color: "#FFFFFF",
                letterSpacing: -0.3,
              }}
            >
              Enregistrer la requête
            </div>
            <div
              style={{
                fontSize: 11,
                color: "rgba(255,255,255,0.5)",
                marginTop: 2,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Sauvegarde · Configuration
            </div>
          </div>
          {/* Badges type / section dans le header */}
          {(typeInfo || sectionInfo) && (
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {typeInfo && (
                <span
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    borderRadius: 8,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    whiteSpace: "nowrap",
                  }}
                >
                  {typeInfo.icon} {typeInfo.label}
                </span>
              )}
              {sectionInfo && (
                <span
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.20)",
                    borderRadius: 8,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.85)",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    whiteSpace: "nowrap",
                  }}
                >
                  {sectionInfo.icon} {sectionInfo.label}
                </span>
              )}
            </div>
          )}
          <button
            onClick={onClose}
            title="Fermer"
            style={{
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.20)",
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: "pointer",
              color: "rgba(255,255,255,0.80)",
              fontSize: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* ── Body ── */}
        <div
          style={{
            padding: "20px 22px",
            display: "flex",
            flexDirection: "column",
            gap: 16,
            overflowY: "auto",
            flex: 1,
          }}
        >
          {/* Résumé de la requête */}
          <div
            style={{
              background: NW.violetSoft,
              border: `1.5px solid ${NW.violetMed}`,
              borderRadius: 12,
              padding: "14px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 700,
                fontSize: 11,
                color: NW.violet,
                textTransform: "uppercase",
                letterSpacing: 1.5,
                marginBottom: 2,
              }}
            >
              ◈ Résumé de la requête
            </div>

            {/* Badges type/section dans le résumé */}
            {(typeInfo || sectionInfo) && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  marginBottom: 2,
                }}
              >
                {typeInfo && (
                  <span
                    style={{
                      background: `${typeInfo.color}18`,
                      border: `1px solid ${typeInfo.color}40`,
                      borderRadius: 6,
                      padding: "3px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: typeInfo.color,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {typeInfo.icon} Vue : {typeInfo.label}
                  </span>
                )}
                {sectionInfo && (
                  <span
                    style={{
                      background: `${sectionInfo.color}14`,
                      border: `1px solid ${sectionInfo.color}35`,
                      borderRadius: 6,
                      padding: "3px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: sectionInfo.color,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {sectionInfo.icon} Affichage : {sectionInfo.label}
                  </span>
                )}
              </div>
            )}

            {/* Mesures */}
            <SummaryRow
              icon="📐"
              label="Mesures"
              items={effectiveMeasures.map(getMeasureCaption)}
              emptyText="Aucune mesure"
              color={NW.violet}
            />

            {/* Dimensions lignes/colonnes — visibles uniquement dans le Query Builder (mdx) */}
            {type === "mdx" &&
              section !== "comparaison" &&
              section !== "comparison" && (
                <>
                  <SummaryRow
                    icon="↔"
                    label="Lignes"
                    items={effectiveRows.map(getDimLabel)}
                    emptyText="Aucune dimension en ligne"
                    color="#1D5BA8"
                  />
                  <SummaryRow
                    icon="↕"
                    label="Colonnes"
                    items={effectiveColumns.map(getDimLabel)}
                    emptyText="Aucune dimension en colonne"
                    color="#00A08C"
                  />
                </>
              )}

            {/* Période — masquée en vue Comparaison */}
            {section !== "comparaison" && section !== "comparison" && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>📅</span>
                <span
                  style={{
                    fontFamily: FONTS.heading,
                    fontWeight: 700,
                    fontSize: 11,
                    color: NW.gray500,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    minWidth: 68,
                    flexShrink: 0,
                  }}
                >
                  Période
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: NW.gray700,
                    fontWeight: 600,
                    background: NW.white,
                    border: `1px solid ${NW.gray200}`,
                    borderRadius: 6,
                    padding: "2px 10px",
                  }}
                >
                  {effectiveDateFrom && effectiveDateTo
                    ? `${effectiveDateFrom} → ${effectiveDateTo}`
                    : effectiveDateFrom
                      ? `Depuis ${effectiveDateFrom}`
                      : effectiveDateTo
                        ? `Jusqu'au ${effectiveDateTo}`
                        : "Toutes les périodes"}
                </span>
                {effectiveTopN && (
                  <span
                    style={{
                      fontSize: 11,
                      color: NW.violet,
                      fontWeight: 700,
                      background: NW.violetLight,
                      border: `1px solid ${NW.violetMed}`,
                      borderRadius: 99,
                      padding: "2px 10px",
                    }}
                  >
                    🏆 Top {effectiveTopN}
                  </span>
                )}
              </div>
            )}

            {/* ── Solde : période de solde ────────────────────────── */}
            {effectiveSoldeCodePeriode && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>🏷️</span>
                <span
                  style={{
                    fontFamily: FONTS.heading,
                    fontWeight: 700,
                    fontSize: 11,
                    color: NW.gray500,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    minWidth: 68,
                    flexShrink: 0,
                  }}
                >
                  Solde
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: "#006357",
                    fontWeight: 700,
                    background: "#D3EFEA",
                    border: "1px solid #A9DFD7",
                    borderRadius: 6,
                    padding: "2px 10px",
                  }}
                >
                  {effectiveSoldeCodePeriode}
                </span>
              </div>
            )}

            {/* ── Comparaison : périodes enrichies (label + dates + solde + démarque) ── */}
            {overrideComparisonPeriods &&
              overrideComparisonPeriods.length > 0 && (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 6 }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span style={{ fontSize: 14, flexShrink: 0 }}>⚖️</span>
                    <span
                      style={{
                        fontFamily: FONTS.heading,
                        fontWeight: 700,
                        fontSize: 11,
                        color: NW.gray500,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Périodes comparées ({overrideComparisonPeriods.length})
                    </span>
                  </div>
                  {/* Table-style header */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "36px 110px 1fr auto auto",
                      gap: "4px 10px",
                      paddingLeft: 22,
                      alignItems: "center",
                    }}
                  >
                    {/* Column headers */}
                    {["", "Période", "Dates", "Solde", "Démarque"].map((h) => (
                      <span
                        key={h}
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: NW.gray500,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        {h}
                      </span>
                    ))}

                    {overrideComparisonPeriods.map((p, i) => {
                      const color = [
                        "#184B8C",
                        "#7DA8D9",
                        "#00A08C",
                        "#D98E04",
                        "#1D5BA8",
                      ][i % 5];
                      return (
                        <React.Fragment key={i}>
                          {/* Badge Px */}
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              color: "#FFFFFF",
                              background: color,
                              borderRadius: 5,
                              padding: "2px 7px",
                              textAlign: "center",
                            }}
                          >
                            P{i + 1}
                          </span>
                          {/* Label */}
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: NW.gray800,
                              background: NW.white,
                              border: `1px solid ${NW.gray200}`,
                              borderRadius: 5,
                              padding: "2px 8px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {p.label || "—"}
                          </span>
                          {/* Dates */}
                          <span
                            style={{
                              fontSize: 11,
                              color: NW.gray500,
                              background: NW.white,
                              border: `1px solid ${NW.gray200}`,
                              borderRadius: 5,
                              padding: "2px 8px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            📅 {p.dateFrom} → {p.dateTo}
                          </span>
                          {/* Solde */}
                          {p.soldeCodePeriode ? (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#006357",
                                background: "#D3EFEA",
                                border: "1px solid #A9DFD7",
                                borderRadius: 5,
                                padding: "2px 8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              🏷️ {p.soldeCodePeriode}
                            </span>
                          ) : (
                            <span style={{ fontSize: 10, color: NW.gray300 }}>
                              —
                            </span>
                          )}
                          {/* Démarque */}
                          {p.demarquePhase ? (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#184B8C",
                                background: NW.violetLight,
                                border: `1px solid ${NW.violetMed}`,
                                borderRadius: 5,
                                padding: "2px 8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              📉 {p.demarquePhase}
                            </span>
                          ) : (
                            <span style={{ fontSize: 10, color: NW.gray300 }}>
                              —
                            </span>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* ── Évolution : config granularité ───────────────────────── */}
            {overrideEvolutionConfig && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>📈</span>
                <span
                  style={{
                    fontFamily: FONTS.heading,
                    fontWeight: 700,
                    fontSize: 11,
                    color: NW.gray500,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    minWidth: 68,
                    flexShrink: 0,
                  }}
                >
                  Évolution
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: NW.gray700,
                    fontWeight: 600,
                    background: NW.white,
                    border: `1px solid ${NW.gray200}`,
                    borderRadius: 6,
                    padding: "2px 10px",
                  }}
                >
                  {overrideEvolutionConfig.dateFrom} →{" "}
                  {overrideEvolutionConfig.dateTo}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: "#00A08C",
                    fontWeight: 700,
                    background: "#D3EFEA",
                    border: "1px solid #A9DFD7",
                    borderRadius: 99,
                    padding: "2px 10px",
                  }}
                >
                  📆{" "}
                  {(
                    {
                      day: "Jour",
                      week: "Semaine",
                      month: "Mois",
                      year: "Année",
                    } as any
                  )[overrideEvolutionConfig.granularity] ??
                    overrideEvolutionConfig.granularity}
                </span>
              </div>
            )}
          </div>

          {/* ── Input nom ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 700,
                fontSize: 12,
                color: NW.gray700,
                letterSpacing: 0.3,
              }}
            >
              Nom de l'enregistrement *
            </label>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (e.target.value.trim()) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
              }}
              placeholder="Ex : Ventes par région Q1 2025"
              maxLength={120}
              style={{
                border: `1.5px solid ${error ? NW.danger : name ? NW.violet : NW.gray300}`,
                borderRadius: 10,
                padding: "10px 14px",
                fontSize: 13,
                fontFamily: FONTS.body,
                fontWeight: 600,
                color: NW.gray800,
                outline: "none",
                background: NW.white,
                transition: "border-color 0.15s",
                boxShadow: name ? `0 0 0 3px ${NW.violetMed}55` : "none",
              }}
            />
            {error && (
              <div
                style={{
                  fontSize: 11,
                  color: NW.danger,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                ⚠ {error}
              </div>
            )}
          </div>

          {/* ── Remarque (NOUVEAU) ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label
              style={{
                fontFamily: FONTS.heading,
                fontWeight: 700,
                fontSize: 12,
                color: NW.gray700,
                letterSpacing: 0.3,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              📝 Remarque
              <span
                style={{
                  fontSize: 10,
                  color: NW.gray500,
                  fontWeight: 400,
                  fontFamily: FONTS.body,
                }}
              >
                (optionnelle)
              </span>
            </label>
            <textarea
              value={remarque}
              onChange={(e) => setRemarque(e.target.value)}
              placeholder="Ajoutez une note, un contexte ou une description…"
              maxLength={500}
              rows={3}
              style={{
                border: `1.5px solid ${remarque ? NW.violet : NW.gray300}`,
                borderRadius: 10,
                padding: "10px 14px",
                fontSize: 12,
                fontFamily: FONTS.body,
                color: NW.gray800,
                outline: "none",
                background: NW.white,
                resize: "vertical",
                minHeight: 72,
                transition: "border-color 0.15s",
                boxShadow: remarque ? `0 0 0 3px ${NW.violetMed}44` : "none",
                lineHeight: 1.6,
              }}
            />
            <div
              style={{
                fontSize: 10,
                color: NW.gray500,
                textAlign: "right",
                fontFamily: FONTS.body,
              }}
            >
              {remarque.length} / 500
            </div>
          </div>

          {/* Succès */}
          {success && (
            <div
              style={{
                background: NW.successLight,
                border: `1.5px solid ${NW.success}44`,
                borderRadius: 10,
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: NW.success,
                fontWeight: 700,
                fontFamily: FONTS.body,
              }}
            >
              <span style={{ fontSize: 16 }}>✅</span>
              Requête enregistrée avec succès !
            </div>
          )}

          {/* Actions */}
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
              marginTop: 2,
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "9px 20px",
                borderRadius: 10,
                border: `1.5px solid ${NW.gray200}`,
                background: NW.white,
                color: NW.gray500,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: FONTS.body,
                transition: "all 0.15s",
              }}
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving || success}
              style={{
                padding: "9px 24px",
                borderRadius: 10,
                border: "none",
                background:
                  saving || success
                    ? NW.gray200
                    : `linear-gradient(135deg,${NW.violetDark} 0%,${NW.violet} 55%,#1D5BA8 100%)`,
                color: saving || success ? NW.gray500 : "#FFFFFF",
                fontSize: 13,
                fontWeight: 700,
                cursor: saving || success ? "not-allowed" : "pointer",
                fontFamily: FONTS.heading,
                boxShadow:
                  saving || success
                    ? "none"
                    : "0 4px 14px rgba(24,75,140,0.30)",
                transition: "all 0.15s",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {saving ? (
                <>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.4)",
                      borderTopColor: "#FFFFFF",
                      display: "inline-block",
                      animation: "nwSpin 0.7s linear infinite",
                    }}
                  />
                  Enregistrement…
                </>
              ) : success ? (
                "✅ Enregistré !"
              ) : (
                <>💾 Enregistrer</>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes nwFadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes nwSlideUp {
          from{opacity:0;transform:translate(-50%,calc(-50% + 18px)) scale(0.96)}
          to  {opacity:1;transform:translate(-50%,-50%) scale(1)}
        }
        @keyframes nwSpin { to{transform:rotate(360deg)} }
      `}</style>
    </>
  );
};

// ── SummaryRow ────────────────────────────────────────────────────────────────
interface SummaryRowProps {
  icon: string;
  label: string;
  items: string[];
  emptyText: string;
  color: string;
}
const SummaryRow: React.FC<SummaryRowProps> = ({
  icon,
  label,
  items,
  emptyText,
  color,
}) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
    <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{icon}</span>
    <span
      style={{
        fontFamily: "Poppins, Inter, system-ui, sans-serif",
        fontWeight: 700,
        fontSize: 11,
        color: "#64748B",
        textTransform: "uppercase",
        letterSpacing: 1,
        minWidth: 68,
        flexShrink: 0,
        paddingTop: 2,
      }}
    >
      {label}
    </span>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, flex: 1 }}>
      {items.length === 0 ? (
        <span style={{ fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>
          {emptyText}
        </span>
      ) : (
        items.map((item, i) => (
          <span
            key={i}
            style={{
              fontSize: 11,
              fontWeight: 700,
              color,
              background: `${color}14`,
              border: `1px solid ${color}30`,
              borderRadius: 5,
              padding: "2px 8px",
              fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            }}
          >
            {item}
          </span>
        ))
      )}
    </div>
  </div>
);

export default SaveQueryModal;
